import * as React from "react";
import { IContextualMenuItem } from "OfficeFabric/ContextualMenu";

import { WikiPage, WikiType } from "TFS/Wiki/Contracts";
import { RepoConstants, WikiActionIds } from "Wiki/Scripts/CommonConstants";
import { FilterableWikiTreeProps, FilterableWikiTree } from "Wiki/Scenarios/Integration/Tree/FilterableWikiTree";
import { WikiPageTreeFilterLazyLoad } from "Wiki/Scenarios/Integration/Tree/WikiPageTreeFilterLazyLoad";
import { ContainerProps } from "Wiki/Scenarios/Overview/Components/OverviewContainer";
import { getTreeItemCommands } from "Wiki/Scenarios/Overview/WikiCommands";
import * as WikiFeatures from "Wiki/Scripts/WikiFeatures";

import "VSS/LoaderPlugins/Css!Wiki/Scenarios/Overview/Components/WikiTreeContainer";

export class WikiTreeContainer extends React.Component<ContainerProps, {}> {
    private _filteredPagesClickCount: number;
    private _selectedPagePath: string;
    private _isEditing: boolean;
    private _filterableWikiTree: FilterableWikiTree;
    private _pageFilter: WikiPageTreeFilterLazyLoad;

    constructor(props: ContainerProps) {
        super(props);

        this._filteredPagesClickCount = 0;
        this._selectedPagePath = this._getSelectedPath(props);
        this._isEditing = this._isEditingState(props);
        this._pageFilter = new WikiPageTreeFilterLazyLoad(true);
    }

    public shouldComponentUpdate(nextProps: ContainerProps): boolean {
        const newSelectedPagePath: string = this._getSelectedPath(nextProps);
        const newIsEditingState: boolean = this._isEditingState(nextProps);
       
        /*
            1. WikiPagesStore and TreeStore are listened by FilterableWikiTree hence not required to re-render container based on that.
            2. All the other props passed to FilterableWikiTree are callbacks which are not expected to change.
            3. So, checking just for the below two props is sufficient to reduce unnecessary re-rendering of this container.
        */
        return this._selectedPagePath !== newSelectedPagePath
            || this._isEditing !== newIsEditingState;
    }

    public render(): JSX.Element {
        this._selectedPagePath = this._getSelectedPath(this.props);
        this._isEditing = this._isEditingState(this.props);

        const filterableWikiTreeProps: FilterableWikiTreeProps = {
            pagesStore: this.props.storesHub.wikiPagesStore,
            treeStore: this.props.storesHub.treeStore,
            selectedFullPath: this._selectedPagePath,
            isEditing: this._isEditing,
            onPageSelected: this._onPageSelected,
            onPageExpand: this.props.actionCreator.expandTreeItem,
            onPageCollapse: this.props.actionCreator.collapseTreeItem,
            getPageCommands: this._getPageCommands,
            onPageKeyDown: this.props.actionCreator.onPageKeyDown,
            getPagesToFilter: this._getPagesToFilter,
            onFilterCleared: this._onFilterCleared,
            onFilteredPageSelected: this._onFilteredPageSelected,
            onDrag: this.props.actionCreator.onPageDrag,
            onDrop: this.props.actionCreator.onPageDrop,
            canDrag: this.props.actionCreator.isValidDragSource,
            canDrop: this.props.actionCreator.isValidDropTarget,
            onOpenInNewTab: this.props.actionCreator.openInNewTab,
            onSearchWithWikiSearchClick: this.props.actionCreator.onSearchWithWikiSearchClick,
            shouldShowWikiSearchSuggestion: WikiFeatures.isWikiSearchEnabled(),
            getPageFilter: this._pageFilter.getPageFilter,
        };

        return <div className={"wiki-tree-container"}>
            <FilterableWikiTree {...filterableWikiTreeProps}
                ref={this._refTree} />
        </div>;
    }

    public setFilterFocus = () => {
        this._filterableWikiTree.focusFilter();
    }

    private _refTree = (filterableWikiTree: FilterableWikiTree) => {
        this._filterableWikiTree = filterableWikiTree;
    }

    private _getPageCommands = (page: WikiPage): IContextualMenuItem[] => {
        const state = this.props.storesHub.state;
        const pagePath = (page && page.path) || RepoConstants.RootPath;
        const isCurrentItem = this._selectedPagePath === pagePath;
        const wikiItem = this.props.storesHub.wikiPagesStore.state.wikiPages[pagePath];
        // In case of undefined permissions, show Edit actions and server will throw error on save in case of no permissions.
        const canEditWiki: boolean = state.sharedState.permissionState.hasContributePermission !== false;
        const isCodeWiki: boolean = state.sharedState.commonState.wiki.type === WikiType.CodeWiki;

        const commandOptions = {
            actionCreator: this.props.actionCreator,
            item: wikiItem,
            isEditing: this._isEditing,
            canEditWiki: canEditWiki,
            isCurrentItem: isCurrentItem,
            isCodeWiki: isCodeWiki,
        };

        return getTreeItemCommands(commandOptions);
    }

    private _onPageSelected = (path: string, depth?: number) => {
        this.props.actionCreator.changePath(path);   
    }

    private _getPagesToFilter = (): IPromise<WikiPage[]> => {
        this._filteredPagesClickCount = 0;
        this.props.actionCreator.telemetrySpy.publishPageFilterPerformed();

        return this.props.actionCreator.getPagesToFilter();
    }

    private _onFilteredPageSelected = (
        path: string,
        query: string,
        isFilteredResult: boolean,
        depth?: number,
        isFuzzyResult?: boolean): void => {

        this._filteredPagesClickCount++;
        this.props.actionCreator.telemetrySpy.publishFilteredPagesClickedCount(this._filteredPagesClickCount, query.length, isFilteredResult, isFuzzyResult);
        this.props.actionCreator.changePath(path);
    }

    private _onFilterCleared = (path: string) => {
        this._filteredPagesClickCount = 0;
        this.props.actionCreator.onFilterCleared(path);
    }

    private _getSelectedPath(props: ContainerProps): string {
        const state = props.storesHub.state;
        const tempPage: WikiPage = props.storesHub.wikiPagesStore.state.tempPage;

        return tempPage ? tempPage.path : state.sharedState.urlState.pagePath; // TODO: remove url dependency here : Task 1419099: remove dependency from urlState
    }

    private _isEditingState(props: ContainerProps): boolean {
        const state = props.storesHub.state;

        return state.sharedState.urlState.action === WikiActionIds.Edit;
    }
}
